import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { banners, getWorkById } from "@/utils/media";
import { useFavorites } from "@/hooks/useFavorites";

const slides = banners
  .map((b) => {
    const work = getWorkById(b.workId);
    return work ? { ...b, work } : null;
  })
  .filter((item): item is NonNullable<typeof item> => item !== null);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const { toggleFavorite, isFavorite } = useFavorites();
  const total = slides.length;

  useEffect(() => {
    if (total <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % total), 6000);
    return () => clearInterval(timer);
  }, [total, index]);

  if (!total) return null;
  const active = slides[index];
  const favorited = isFavorite(active.work.id);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[440px] w-full md:h-[560px]">
        <img
          key={active.id}
          src={active.work.backdrop}
          alt={`${active.work.title} 剧照`}
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent" />

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-14 md:px-8 md:pb-16">
            <div key={`${active.id}-text`} className="animate-rise max-w-xl">
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                <i className="ri-fire-line" />
                {active.kicker}
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-5xl">
                {active.work.title}
              </h1>
              <p className="mt-2 text-sm text-background-100/90 md:text-base">
                {active.headline} · {active.work.year} · {active.work.region} · {active.work.genres.join(" / ")}
              </p>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-background-100/80 md:text-base">
                {active.description}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.work.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-fill text-lg" />
                  查看详情
                </Link>
                <button
                  type="button"
                  onClick={() => toggleFavorite(active.work.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-semibold transition-colors ${
                    favorited
                      ? "border-secondary-300 bg-secondary-100 text-secondary-800"
                      : "border-background-50/40 text-background-50 hover:bg-background-50/10"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                  {favorited ? "已收藏" : "收藏"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => setIndex((i) => (i - 1 + total) % total)}
          className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:left-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => setIndex((i) => (i + 1) % total)}
          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:right-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>
      </div>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到 ${slide.work.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-7 bg-accent-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}